import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { useGetIdentity } from "ra-core";
import { SquaresFour } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ModuleSetupPrompt } from "@/modules/apps/ModuleSetupPrompt";
import {
  isModuleEnabled,
  MODULES,
  moduleForPath,
  type OrgModulesState,
} from "@/modules/registry";

/**
 * Hides pages of modules the company has switched off (typed URL, old link,
 * bookmark). Data is untouched; turning the module back on shows it again.
 */
export const ModuleRouteGuard = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { identity } = useGetIdentity();
  const moduleId = moduleForPath(pathname);
  const orgModules = (identity as { org_modules?: OrgModulesState | null })
    ?.org_modules;

  if (!moduleId) return <>{children}</>;
  if (isModuleEnabled(orgModules, moduleId)) {
    return (
      <>
        {children}
        <ModuleSetupPrompt key={moduleId} moduleId={moduleId} />
      </>
    );
  }

  const module = MODULES[moduleId];
  const isAdmin = (identity as { administrator?: boolean })?.administrator;
  return (
    <div className="flex min-h-[50vh] flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      <module.icon className="size-10 text-muted-foreground" />
      <h1 className="text-base font-semibold">
        {module.name} isn&apos;t turned on
      </h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        {isAdmin
          ? `Turn on ${module.name} in Apps to use it here.`
          : `Ask your administrator to turn on ${module.name}.`}
      </p>
      {isAdmin ? (
        <Button asChild size="sm">
          <Link to={`/apps/${moduleId}`}>
            <SquaresFour className="size-4" />
            Open Apps
          </Link>
        </Button>
      ) : null}
    </div>
  );
};
